import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCard } from '../patterns';
import type { LongRunningSession } from '../../services/sprayDashboardService';
import type { LicenseAlert, CalibrationAlert } from '../../utils/sprayDashboardMath';

const TOP = 3;

export interface SprayAlertStackProps {
  longRunning:   LongRunningSession[];
  licenses:      LicenseAlert[];
  calibrations:  CalibrationAlert[];
}

export const SprayAlertStack: React.FC<SprayAlertStackProps> = ({ longRunning, licenses, calibrations }) => {
  const { t } = useTranslation();
  return (
    <div className="spray-dashboard__alert-stack" data-testid="spray-alert-stack">
      {longRunning.length > 0 && (
        <AlertCard
          title={t('spray.dashboard.alerts.longRunning.title')}
          count={longRunning.length}
          tone="danger"
          viewAllHref="/protection/sessions?filter=longRunning"
        >
          <ul className="spray-dashboard__alert-list">
            {longRunning.slice(0, TOP).map(row => (
              <li key={String(row._id)}>
                {row.programName ?? '—'} — {t('spray.dashboard.alerts.longRunning.daysRunning', { days: row.daysRunning })}
              </li>
            ))}
          </ul>
        </AlertCard>
      )}

      {licenses.length > 0 && (
        <AlertCard
          title={t('spray.dashboard.alerts.licenseExpiry.title')}
          count={licenses.length}
          tone="warn"
          viewAllHref="/protection/operators?filter=expiring"
        >
          <ul className="spray-dashboard__alert-list">
            {licenses.slice(0, TOP).map(row => (
              <li key={row._id}>
                {row.name} — {row.expired
                  ? t('spray.dashboard.alerts.licenseExpiry.expired')
                  : t('spray.dashboard.alerts.licenseExpiry.expiringInDays', { days: row.daysUntilExpiry })}
              </li>
            ))}
          </ul>
        </AlertCard>
      )}

      {calibrations.length > 0 && (
        <AlertCard
          title={t('spray.dashboard.alerts.calibration.title')}
          count={calibrations.length}
          tone="warn"
          viewAllHref="/protection/machines?filter=calibrationDue"
        >
          <ul className="spray-dashboard__alert-list">
            {calibrations.slice(0, TOP).map(row => (
              <li key={row._id}>
                {row.name} — {row.neverCalibrated
                  ? t('spray.dashboard.alerts.calibration.neverCalibrated')
                  : row.overdue
                    ? t('spray.dashboard.alerts.calibration.overdue')
                    : t('spray.dashboard.alerts.calibration.dueInDays', { days: row.daysUntilDue })}
              </li>
            ))}
          </ul>
        </AlertCard>
      )}
    </div>
  );
};

export default SprayAlertStack;
